import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import LoginHeader from "../Login/LoginHeader";
import CompanyDetailsForm from "./CompanyDetailsForm";
import ProcurementRoleForm from "./ProcurementRoleForm";
import SupplierDiversityObjectivesForm from "./SupplierDiversityObjectivesForm";
import RegistrationConfirmation from "./RegistrationConfirmation";
import PersonalInfoForm from "../../Registration/PersonalInfoForm";
import ReviewPage from "./ReviewPage";
import { useSelector } from "react-redux";

const steps = [
  {
    id: 1,
    name: "Personal Details",
    slug: "personal-details",
    component: PersonalInfoForm,
  },
  {
    id: 2,
    name: "Company Details",
    slug: "company-details",
    component: CompanyDetailsForm,
  },
  {
    id: 3,
    name: "Procurement Role",
    slug: "procurement-role",
    component: ProcurementRoleForm,
  },
  {
    id: 4,
    name: "Supplier Diversity",
    slug: "supplier-diversity",
    component: SupplierDiversityObjectivesForm,
  },
  {
    id: 5,
    name: "Review",
    slug: "review",
    component: ReviewPage,
  },
  {
    id: 6,
    name: "Registration Confirmation",
    slug: "registration-confirmation",
    component: RegistrationConfirmation,
  },
];

const BuyerProfileRegistration = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { stepSlug } = useParams();

  const currentStep = steps.findIndex((step) => step.slug === stepSlug);

  useEffect(() => {
    if (currentStep === -1) {
      navigate(`/buyer-profile-registration/${steps[0].slug}`, {
        replace: true,
      });
    }
  }, [currentStep, navigate]);

  if (currentStep === -1) return null;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      navigate(`/buyer-profile-registration/${steps[currentStep + 1].slug}`);
    }
  };

  const handleSkip = () => {
    if (currentStep < steps.length - 1) {
      navigate(`/buyer-profile-registration/${steps[currentStep + 1].slug}`);
    }
  };

  const CurrentComponent = steps[currentStep].component;

  return (
    <div className="flex min-h-screen bg-white">
      <LoginHeader isAuthenticated={isAuthenticated} registrationPage={true} />
      <div className="flex flex-col fixed left-2 sm:left-4 md:left-6 lg:left-8 top-[50%] transform -translate-y-1/2">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isActive = index === currentStep;
          const iconColor = isCompleted || isActive ? "#08F" : "#A7A7A7";
          const textColor =
            isCompleted || isActive ? "text-[#08F]" : "text-[#A7A7A7]";

          return (
            <div key={step.id} className="flex items-center p-2">
              <div className="flex items-center relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="13"
                  viewBox="0 0 10 10"
                  fill="none"
                  style={{ zIndex: 100 }}
                  className="align-middle"
                  onClick={() => {
                    if (isCompleted) {
                      navigate(`/buyer-profile-registration/${step.slug}`);
                    }
                  }}
                >
                  <path
                    d="M5 9.5C7.48528 9.5 9.5 7.48528 9.5 5C9.5 2.51472 7.48528 0.5 5 0.5C2.51472 0.5 0.5 2.51472 0.5 5C0.5 7.48528 2.51472 9.5 5 9.5Z"
                    fill="white"
                    stroke={iconColor}
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.5 3.99951L4.5 5.99951L3.5 4.99951"
                    stroke={iconColor}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                {index !== steps.length - 1 && (
                  <div
                    className={`absolute top-0 bottom-0 left-[calc(50%)] w-px h-10 ${
                      index < currentStep ? "bg-[#08F]" : "bg-[#A7A7A7]"
                    }`}
                  />
                )}
              </div>

              <div
                className="cursor-pointer items-center ml-2 hidden xl:block"
                onClick={() => {
                  if (isCompleted) {
                    navigate(`/buyer-profile-registration/${step.slug}`);
                  }
                }}
              >
                <p
                  className={`text-sm font-semibold ${textColor} align-middle`}
                >
                  {step.name}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex-1 flex justify-center items-center pt-[80px] md:pt-[100px]">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-[800px] p-6"
        >
          <CurrentComponent onNext={handleNext} onSkip={handleSkip}  onEditStep={(slug) => navigate(`/buyer-profile-registration/${slug}`)}  />
        </motion.div>
      </div>
    </div>
  );
};

export default BuyerProfileRegistration;
